/**
 * Beats: how a bar's slots share its length.
 *
 * A bar is one measure. When it holds several slots, every slot but the last
 * stores `beats`, its length in the time signature's denominator units (a
 * quarter in 4/4, an eighth in 6/8, a half in 2/2 — the unit `MeterInfo`
 * counts in), and the last slot takes whatever is left, storing nothing. A
 * bar holding one slot stores nothing either: the slot is the whole bar.
 *
 * Lengths come in halves of a unit (`BEAT_STEP`), never below half a unit,
 * except in /8 and /16 meters, whose unit is already an eighth or shorter:
 * there only whole units are used (`allowsHalfBeats`).
 *
 * `layoutBarSlots` is the one place a slot's length is worked out. The editor
 * only ever writes bars that satisfy the rules above; data from elsewhere
 * (a file, Johan, an older app) is corrected on read here, never rewritten:
 * a middle slot with no beats takes one unit, lengths are clamped so every
 * later slot keeps its minimum, and a bar whose slots cannot fit grows to
 * hold them rather than losing one.
 */
import { getMeterInfo } from './meterGrouping.js';
import { normalizeSongTimeSignature } from './songMeta.js';
/** Beats are set in halves of a unit. */
export const BEAT_STEP = 0.5;
/** The shortest a slot can be, in units, where halves are allowed. */
export const MIN_SLOT_BEATS = BEAT_STEP;
/** True when the meter's unit can be halved: /1, /2 and /4 meters. */
export function allowsHalfBeats(timeSignature) {
    return timeSignature.denominator <= 4;
}
/** The shortest a slot can be in this meter: half a unit, or a whole one in /8 and /16. */
export function getMinSlotBeats(timeSignature) {
    return allowsHalfBeats(timeSignature) ? MIN_SLOT_BEATS : 1;
}
/** Bar length in denominator units: the numerator (4/4 → 4, 6/8 → 6). */
export function getBarUnits(timeSignature) {
    return timeSignature.numerator;
}
/**
 * A stored beats value cleaned: a positive multiple of `BEAT_STEP` stays,
 * anything else (absent, null, 0, negative, a third of a beat, NaN) is null,
 * "no length set". Never throws.
 */
export function normalizeSlotBeats(value) {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < MIN_SLOT_BEATS) {
        return null;
    }
    return Number.isInteger(value / BEAT_STEP) ? value : null;
}
/** A length in units for display: 2 → "2", 2.5 → "2½", 0.5 → "½". */
export function formatBeats(units) {
    const whole = Math.floor(units);
    const half = units - whole >= BEAT_STEP;
    if (whole === 0) {
        return half ? '½' : '0';
    }
    return `${whole}${half ? '½' : ''}`;
}
/** The beats a slot stores, cleaned. Read beats through this, never the raw field. */
export function getSlotBeats(slot) {
    return normalizeSlotBeats(slot.beats);
}
/** True when the bar holds more than one slot. */
export const isSplitBar = (bar) => bar.slots.length > 1;
/** True when the bar is split or any of its slots stores beats: what a time-signature change would clear. */
export const barHasBeats = (bar) => isSplitBar(bar) || bar.slots.some(slot => getSlotBeats(slot) !== null);
export const rowHasBeats = (row) => row.bars.some(barHasBeats);
export const sectionsHaveBeats = (sections) => sections.some(section => section.rows.some(rowHasBeats));
/** A stored value as this meter can play it: halves rounded up to a unit where halves aren't allowed. */
function storedLength(beats, minUnits) {
    if (beats === null) {
        return null;
    }
    return minUnits === 1 ? Math.max(1, Math.ceil(beats)) : beats;
}
/**
 * Where each slot of a bar starts and how long it is, in denominator units.
 * The sum is the bar's length: normally `getBarUnits`, longer only when the
 * slots cannot fit (see the file comment).
 */
export function layoutBarSlots(bar, timeSignature) {
    const units = getBarUnits(timeSignature);
    const minUnits = getMinSlotBeats(timeSignature);
    const lastIndex = bar.slots.length - 1;
    const layout = [];
    let start = 0;
    bar.slots.forEach((slot, index) => {
        const isLast = index === lastIndex;
        let lengthUnits;
        if (isLast) {
            lengthUnits = Math.max(minUnits, units - start);
        }
        else {
            const wanted = storedLength(getSlotBeats(slot), minUnits) ?? 1;
            // What this slot may take while every later slot keeps its minimum.
            const room = units - start - (lastIndex - index) * minUnits;
            lengthUnits = Math.max(minUnits, Math.min(wanted, room));
        }
        layout.push({ slotId: slot.id, startUnits: start, lengthUnits, isLast });
        start += lengthUnits;
    });
    return layout;
}
/**
 * A slot's beats as dots (the Beats display): one per unit, and a half-width
 * dot for each half unit, in the order they fall in the bar (a slot starting
 * on an "and" opens with its half). Meters that group their units (6/8 → 3+3,
 * 7/8 → 2+2+3) mark where a group starts, drawn after a wider gap.
 */
export function buildBeatDots(layout, timeSignature) {
    const { groups } = getMeterInfo(timeSignature);
    const grouped = groups.some((size) => size > 1);
    const groupStarts = new Set();
    groups.reduce((at, size) => {
        groupStarts.add(at);
        return at + size;
    }, 0);
    const dots = [];
    const end = layout.startUnits + layout.lengthUnits;
    let at = layout.startUnits;
    while (at < end) {
        const next = Math.min(end, Math.floor(at) + 1);
        dots.push({ half: next - at < 1, groupStart: grouped && dots.length > 0 && groupStarts.has(at) });
        at = next;
    }
    return dots;
}
/** The bar's length in units: `getBarUnits` unless its slots need more. */
export function getBarLengthUnits(bar, timeSignature) {
    return layoutBarSlots(bar, timeSignature).reduce((sum, slot) => sum + slot.lengthUnits, 0);
}
/** Common time: what a row plays in when nothing sets a meter. */
export const DEFAULT_TIME_SIGNATURE = Object.freeze({ numerator: 4, denominator: 4 });
/**
 * The meter each row is in, by row id: the global time signature when set;
 * otherwise a row's own signature stays in force until a later row sets
 * another, in reading order across sections (the rule playback follows;
 * rows before the first signature are in 4/4). A repeated section's second
 * pass can differ in playback when the section changes meter inside; the
 * editor and the PDF show the first pass.
 */
export function getRowMeters(sections, globalTimeSignature) {
    const meters = new Map();
    let rowMeter = DEFAULT_TIME_SIGNATURE;
    for (const section of sections) {
        for (const row of section.rows) {
            rowMeter = normalizeSongTimeSignature(row.timeSignature) ?? rowMeter;
            meters.set(row.id, globalTimeSignature ?? rowMeter);
        }
    }
    return meters;
}
